/**
 * /learn/certificates — the member's completion certificates.
 *
 * Certificates are issued eagerly on course/program completion; this endpoint
 * also reconciles idempotently, so the list self-heals. Renders the four states
 * explicitly: loading (skeleton), error (retry), empty, and the card grid.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { useCertificates } from '../../hooks/learner/useCertificates';
import CertificateCard from '../../components/learner/CertificateCard';

const CertificatesPage: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const { certificates, isLoading, isError, refetch } = useCertificates();

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
        <div className="h-48 rounded-xl bg-neutral-200 dark:bg-neutral-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Certificates" root={{ label: 'Home', to: '/learn/dashboard' }} />

      {isError ? (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <p className="text-sm text-text-secondary">
            We couldn&apos;t load your certificates. Please try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center justify-center rounded-full bg-primary-600 dark:bg-primary-350 px-4 py-2 text-sm font-medium transition-[transform,background-color] duration-150 ease-out hover:bg-primary-700 dark:hover:bg-primary-400 active:scale-[0.97]"
          >
            Try again
          </button>
        </div>
      ) : certificates.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
            <svg
              className="h-8 w-8 text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <p className="max-w-xs text-sm text-text-secondary">
            Certificates will appear here once you complete a course or program that awards one.
          </p>
          <a
            href="/learn/courses"
            className="text-sm font-bold text-primary-500 hover:underline"
          >
            Continue a course →
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {certificates.map((certificate, index) => (
            <motion.div
              key={certificate.id}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.25,
                ease: EASE_OUT,
                delay: reduceMotion ? 0 : Math.min(index * 0.05, 0.3),
              }}
            >
              <CertificateCard certificate={certificate} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CertificatesPage;
